/**
 * theme.js — palette and primitives, ported from the web app.
 *
 * ─── Ported verbatim, not approximated ──────────────────────────────────────
 * The first version of this file used values recalled from earlier work —
 * #7bc27e green, #c4614a red, a fixed #f5a623 accent. All three were wrong.
 * The web uses #34d399 / #f472b6, and its comment is explicit: "P&L — LOCKED,
 * never tier-shift". Those two colours mean profit and loss across the whole
 * product, so they must not drift between surfaces.
 *
 * ─── The theme is tier-driven ───────────────────────────────────────────────
 * Six tiers derived from lifetime live volume, each with its own accent and
 * four background shades. Hardcoding TRADER amber meant a GHOST or ENTRY user
 * saw a colour scheme that was not theirs — the web would show #b08d57 on
 * #0a0806 while the mini app showed bright amber. Same account, two products.
 *
 * applyTier() sets CSS variables, so every surface moves together the way it
 * does on the web.
 */

// P&L colours. Locked — identical on every surface, never tier-shifted.
export const PNL = {
  green: "#34d399",
  rose:  "#f472b6",
};

export const VOL_TIERS = [
  { name: "GHOST",  min: 0,       color: "#9c8a6a" },
  { name: "ENTRY",  min: 1,       color: "#b08d57" },
  { name: "ACTIVE", min: 5000,    color: "#c9a94e" },
  { name: "TRADER", min: 50000,   color: "#f5a623" },
  { name: "WHALE",  min: 250000,  color: "#3fb9b0" },
  { name: "LEGEND", min: 1000000, color: "#d8c8a0" },
];

export const TIER_THEME = {
  // bg1 lifted ~2 stops at the low tiers. On the web these sit against a
  // large canvas where a 6-unit difference is legible; on a phone the card
  // and the page looked like the same surface.
  GHOST:  { accent: "#9c8a6a", bg0: "#0a0806", bg1: "#15110c", bg2: "#1c160f", bg3: "#231c14" },
  ENTRY:  { accent: "#b08d57", bg0: "#0a0806", bg1: "#15110b", bg2: "#1c160e", bg3: "#231b12" },
  ACTIVE: { accent: "#c9a94e", bg0: "#0a0906", bg1: "#15120b", bg2: "#1c180e", bg3: "#231e12" },
  TRADER: { accent: "#f5a623", bg0: "#080705", bg1: "#0d0b08", bg2: "#12100c", bg3: "#161310" },
  WHALE:  { accent: "#3fb9b0", bg0: "#050f0e", bg1: "#081413", bg2: "#0a1917", bg3: "#0d1e1c" },
  LEGEND: { accent: "#d8c8a0", bg0: "#0a0806", bg1: "#100d09", bg2: "#16120c", bg3: "#1b160f" },
};

export const volTier = (v) => {
  let cur = VOL_TIERS[0];
  for (const t of VOL_TIERS) if ((v || 0) >= t.min) cur = t;
  return cur;
};

/** Set the CSS variables for a tier. Mirrors themeVars() on the web. */
export function applyTier(tierName) {
  const t = TIER_THEME[tierName] || TIER_THEME.TRADER;
  const rgb = t.accent.replace("#", "").match(/../g)
    .map((h) => parseInt(h, 16)).join(",");
  const r = document.documentElement.style;
  r.setProperty("--pe-accent", t.accent);
  r.setProperty("--pe-bg0", t.bg0);
  r.setProperty("--pe-bg1", t.bg1);
  r.setProperty("--pe-bg2", t.bg2);
  r.setProperty("--pe-bg3", t.bg3);
  // Hairlines raised from 0.10/0.22. At 0.10 on a near-black surface the
  // borders effectively vanish on a phone, so every panel loses its edge and
  // the layout reads as one flat mass.
  r.setProperty("--pe-line", `rgba(${rgb},0.20)`);
  r.setProperty("--pe-line2", `rgba(${rgb},0.38)`);
  document.body.style.background = t.bg0;
  return t;
}

// Mirrors the web's C constant, including the CSS-var indirection so tier
// changes propagate without re-rendering every component.
export const C = {
  bg0:   "var(--pe-bg0, #080705)",
  bg1:   "var(--pe-bg1, #0d0b08)",
  bg2:   "var(--pe-bg2, #12100c)",
  bg3:   "var(--pe-bg3, #161310)",
  line:  "var(--pe-line, rgba(245,166,35,0.20))",
  line2: "var(--pe-line2, rgba(245,166,35,0.38))",
  // Text lifted from the web's values. The web renders on a large screen at
  // arm's length; a phone in a webview is smaller, often outdoors, and the
  // original #8a7550 secondary against #0a0806 reads as washed out — most
  // visibly at GHOST and ENTRY, where the accent is muted by design and
  // cannot carry the contrast on its own.
  //
  // The accent stays exactly as the tier defines it. Only the neutrals move.
  ink:   "#f0dfc0",
  sub:   "#bda87e",
  dim:   "#a08c5a",
  amber: "var(--pe-accent, #f5a623)",
  green: PNL.green,
  rose:  PNL.rose,
  red:   "#ef4444",
  violet:"#a78bfa",
  cyan:  "#22d3ee",
  disp:  "'Bebas Neue', sans-serif",
  mono:  "'IBM Plex Mono', monospace",
  // aliases kept so existing screens compile unchanged
  border:   "var(--pe-line, rgba(245,166,35,0.20))",
  borderHi: "var(--pe-line2, rgba(245,166,35,0.38))",
};

/** Exact port of the web's fUsd — K/M abbreviation and all. */
export const fUsd = (n, sign = false) => {
  if (n == null || isNaN(n)) return "—";
  const abs = Math.abs(n);
  const s = sign ? (n >= 0 ? "+" : "-") : (n < 0 ? "-" : "");
  if (abs >= 1e6) return `${s}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${s}$${(abs / 1e3).toFixed(1)}K`;
  return `${s}$${abs.toFixed(2)}`;
};

export const money = fUsd;   // alias for existing call sites

export const pnlColor = (v) =>
  Number(v || 0) > 0 ? C.green : Number(v || 0) < 0 ? C.rose : C.sub;

export const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "—");

export const Label = ({ children, style }) => (
  <div style={{ fontSize: 11, color: C.dim, letterSpacing: "0.16em",
                textTransform: "uppercase", marginBottom: 8, ...style }}>
    {children}
  </div>
);

export const Panel = ({ children, style, accent, onClick, className = "" }) => (
  <div
    onClick={onClick}
    role={onClick ? "button" : undefined}
    className={`${onClick ? "pe-tap " : ""}${className}`.trim()}
    style={{
      border: `1px solid ${C.line}`, background: C.bg1,
      padding: "11px 12px",
      ...(accent ? { borderLeft: `2px solid ${accent}` } : {}),
      ...(onClick ? { cursor: "pointer", touchAction: "manipulation",
                      WebkitTapHighlightColor: "transparent" } : {}),
      ...style,
    }}
  >
    {children}
  </div>
);

export const Row = ({ label, value, color, last }) => (
  <div style={{ display: "flex", justifyContent: "space-between",
                padding: "10px 12px", gap: 10,
                borderBottom: last ? "none" : `1px solid ${C.line}` }}>
    <span style={{ fontSize: 12, color: C.dim, flexShrink: 0 }}>{label}</span>
    <span style={{ fontSize: 12, color: color || C.ink, textAlign: "right",
                   overflowWrap: "anywhere" }}>{value}</span>
  </div>
);

export const Button = ({ children, onClick, variant = "primary", disabled, style }) => {
  const base = {
    padding: 13, width: "100%", fontFamily: C.mono, fontSize: 12,
    letterSpacing: "0.14em", textTransform: "uppercase",
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.45 : 1,
    touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
    minHeight: 44, borderRadius: 2,
  };
  const variants = {
    primary: { background: C.amber, color: "#0a0806", border: "none", fontWeight: 700 },
    ghost:   { background: "transparent", color: C.sub, border: `1px solid ${C.line2}` },
    danger:  { background: "transparent", color: C.rose, border: `1px solid ${C.rose}55` },
    quiet:   { background: "transparent", color: C.dim, border: `1px solid ${C.line}` },
  };
  return (
    <button onClick={disabled ? undefined : onClick} disabled={disabled}
            style={{ ...base, ...variants[variant], ...style }}>
      {children}
    </button>
  );
};

export const Segment = ({ options, value, onChange, style }) => (
  <div style={{ display: "grid",
                gridTemplateColumns: `repeat(${options.length},1fr)`,
                gap: 1, background: C.line, border: `1px solid ${C.line}`,
                ...style }}>
    {options.map((o) => {
      const on = o.value === value;
      return (
        <div key={o.value} onClick={() => onChange(o.value)} className="pe-tap"
             style={{
               background: on ? `${o.color || C.amber}18` : C.bg1,
               color: on ? (o.color || C.amber) : C.dim,
               boxShadow: on ? `inset 0 0 0 1px ${o.color || C.amber}` : "none",
               padding: "10px 6px", textAlign: "center", fontSize: 11,
               letterSpacing: "0.12em", textTransform: "uppercase",
               cursor: "pointer", touchAction: "manipulation",
               WebkitTapHighlightColor: "transparent",
             }}>
          {o.label}
        </div>
      );
    })}
  </div>
);

export const Empty = ({ children }) => (
  <div style={{ padding: "40px 20px", textAlign: "center", fontSize: 12.5,
                color: C.dim, lineHeight: 1.7 }}>
    {children}
  </div>
);

export const Chip = ({ children, color = C.dim }) => (
  <span style={{ fontSize: 10, color, border: `1px solid ${color}44`,
                 padding: "3px 8px", letterSpacing: "0.08em",
                 whiteSpace: "nowrap", borderRadius: 2 }}>
    {children}
  </span>
);

export const Skeleton = ({ w = "100%", h = 14, style }) => (
  <div className="pe-skeleton"
       style={{ width: w, height: h, borderRadius: 2, ...style }} />
);

export const SkeletonRow = () => (
  <div style={{ border: `1px solid ${C.line}`, background: C.bg1,
                padding: "11px 12px", marginBottom: 7 }}>
    <div style={{ display: "flex", justifyContent: "space-between",
                  marginBottom: 8 }}>
      <Skeleton w={110} h={13} />
      <Skeleton w={64} h={13} />
    </div>
    <Skeleton w={150} h={10} />
  </div>
);

/** Compact tier crest. The web's version is a beveled SVG-clipped badge;
 *  this keeps the colour and the name at a size that fits a phone header. */
export const TierBadge = ({ tierName }) => {
  const t = TIER_THEME[tierName] || TIER_THEME.TRADER;
  const idx = Math.max(0, VOL_TIERS.findIndex((x) => x.name === tierName));
  return (
    <span style={{
      fontFamily: C.disp, fontSize: 13, letterSpacing: "0.12em",
      color: t.accent, border: `1px solid ${t.accent}55`,
      background: `${t.accent}14`, padding: "3px 9px", borderRadius: 2,
      boxShadow: `0 0 ${4 + idx * 2}px ${t.accent}33`, whiteSpace: "nowrap",
    }}>
      {tierName}
    </span>
  );
};
